import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  useTheme,
  Fab
} from '@mui/material';
import {
  Phone,
  Mic,
  MicOff,
  VolumeUp,
  Send
} from '@mui/icons-material';
import { useSocket } from '../contexts/SocketContext';
import { useVoice } from '../contexts/VoiceContext';

const PhoneCall = () => {
  const theme = useTheme();
  const { sendMessage, messages } = useSocket();
  const { 
    isListening, 
    startListening, 
    stopListening, 
    transcript, 
    fullTranscript,
    speak,
    isSupported 
  } = useVoice();
  
  const [callActive, setCallActive] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');

  const handleCallStart = () => {
    setCallActive(true);
    speak('Kaixo! Soy BASERRI-ADITU, tu asistente de voz para el Gobierno Vasco. Puedo ayudarte con guías, movimientos, alertas sanitarias, censo y salud de tus animales. ¿En qué puedo ayudarte hoy?');
  };

  const handleSendMessage = () => {
    if (currentMessage.trim()) {
      sendMessage(currentMessage);
      
      // Respuesta inteligente basada en el mensaje
      const lowerMessage = currentMessage.toLowerCase();
      let response = '';
      
      if (lowerMessage.includes('guía') || lowerMessage.includes('movimiento')) {
        response = 'Tienes 3 guías en curso y 156 emitidas este mes. Para generar una nueva guía, necesito saber qué animales quieres mover y a dónde. ¿Es para matadero, otra explotación o venta?';
      } else if (lowerMessage.includes('nacimiento') || lowerMessage.includes('parido') || lowerMessage.includes('ha parido')) {
        response = '¡Perfecto! Para registrar un nacimiento, necesito el número de crotal de la madre y del recién nacido. ¿Puedes darme el crotal de la madre?';
      } else if (lowerMessage.includes('alerta') || lowerMessage.includes('sanitario')) {
        response = 'Tienes 2 alertas activas: 1 vacunación antirrábica pendiente para 15 animales y 1 restricción de movimiento en zona A. ¿Quieres detalles de alguna alerta?';
      } else if (lowerMessage.includes('censo') || lowerMessage.includes('animales') || lowerMessage.includes('cuántos')) {
        response = 'Tu explotación BASERRI GOVERNMENT DEMO tiene 490 animales: 320 bovinos, 150 ovinos y 20 caprinos. 450 están sanos, 35 en tratamiento y 5 con observación. ¿Necesitas el desglose por especie?';
      } else if (lowerMessage.includes('tratamiento') || lowerMessage.includes('medicina')) {
        response = 'Tienes 5 tratamientos en curso: 2 antibióticos, 1 antiparasitario y 2 vacunas. ¿Quieres ver el historial de algún animal específico?';
      } else if (lowerMessage.includes('hola') || lowerMessage.includes('kaixo')) {
        response = 'Kaixo! ¿Cómo estás hoy? ¿Necesitas ayuda con el censo, algún movimiento, revisar alertas sanitarias o gestionar tratamientos?';
      } else {
        response = 'Entendido. Como tu asistente BASERRI-ADITU del Gobierno Vasco, puedo ayudarte con: guías de movimiento, registro de nacimientos, alertas sanitarias, censo de animales, tratamientos y salud. ¿Qué necesitas hacer?';
      }
      
      setTimeout(() => {
        speak(response);
      }, 1000);
      setCurrentMessage('');
    }
  };

  const handleCallEnd = () => {
    setCallActive(false);
    setCurrentMessage('');
  };

  const handleVoiceMessage = () => {
    if (fullTranscript.trim()) {
      const msgText = fullTranscript.trim();
      setCurrentMessage(msgText);
      sendMessage(msgText); // Sending string as expected by SocketContext

      // Generate local voice response
      const response = generateResponse(msgText);
      speak(response);
      setCurrentMessage(response);
    }
  };

  const generateResponse = (message) => {
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('hola') || lowerMessage.includes('kaixo')) {
      return 'Kaixo! ¿Cómo estás hoy? ¿Necesitas ayuda con el censo, algún movimiento o revisar las alertas sanitarias?';
    }
    
    if (lowerMessage.includes('censo')) {
      return 'Tu explotación tiene 490 animales: 320 bovinos, 150 ovinos y 20 caprinos. ¿Quieres ver el desglose completo por especie?';
    }
    
    if (lowerMessage.includes('movimiento')) {
      return 'Tienes 3 movimientos programados para esta semana. ¿Quieres registrar un nuevo movimiento o ver los existentes?';
    }
    
    if (lowerMessage.includes('alerta')) {
      return 'Tienes 2 alertas activas: una vacunación pendiente y una restricción sanitaria. ¿Quieres ver los detalles?';
    }
    
    if (lowerMessage.includes('guía')) {
      return 'Tienes 15 guías emitidas este mes y 3 en curso. ¿Necesitas emitir una nueva guía?';
    }
    
    if (lowerMessage.includes('salud')) {
      return '450 animales están sanos, 35 en tratamiento y 5 con observación. ¿Quieres ver el historial de tratamientos?';
    }
    
    return 'Entendido. Puedo ayudarte con el censo, movimientos, alertas, guías, salud de los animales o cualquier otra gestión de tu explotación. ¿Qué necesitas hacer?';
  };

  const handleTextMessage = () => {
    if (currentMessage.trim()) {
      sendMessage(currentMessage.trim()); // Sending string as expected by SocketContext
      setCurrentMessage('');
    }
  };

  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      {/* Header */}
      <Card 
        elevation={3} 
        sx={{ 
          p: 2, 
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: '#059669', width: 60, height: 60, mr: 2 }}>
            <Phone sx={{ fontSize: 30, color: 'white' }} />
          </Avatar>
          <Typography variant="h6" sx={{ color: '#333', fontWeight: 'bold' }}>
            BASERRI-ADITU
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', ml: 2 }}>
            {callActive ? 'En llamada...' : 'Asistente de Voz'}
          </Typography>
        </Box>
        
        {/* Call Controls */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          {!callActive ? (
            <Button 
              variant="contained" 
              color="error" 
              startIcon={<Phone />}
              onClick={handleCallStart}
              sx={{ minWidth: 120 }}
            >
              Iniciar Llamada
            </Button>
          ) : (
            <Button 
              variant="contained" 
              color="success" 
              startIcon={<Phone />}
              onClick={handleCallEnd}
              sx={{ minWidth: 120 }}
            >
              Colgar
            </Button>
          )}
        </Box>
      </Card>

      {/* Messages Area */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          {messages.map((msg, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              {msg.type === 'voice_command' ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Card sx={{ 
                    p: 2, 
                    maxWidth: '70%',
                    backgroundColor: '#e3f2fd',
                    borderRadius: 2
                  }}>
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      {msg.message}
                    </Typography>
                  </Card>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Card sx={{ 
                    p: 2, 
                    maxWidth: '70%',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2
                  }}>
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      {msg.message}
                    </Typography>
                  </Card>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Input Area */}
      <Card 
        elevation={3} 
        sx={{ 
          p: 2, 
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            color={isListening ? 'error' : 'primary'}
            onClick={isListening ? stopListening : startListening}
            disabled={!callActive}
            startIcon={isListening ? <MicOff /> : <Mic />}
            sx={{
              background: isListening ? '#ff4444' : '#059669',
              '&:hover': {
                background: isListening ? '#cc0000' : '#1976d2'
              }
            }}
          >
            {isListening ? 'Grabando...' : 'Micrófono'}
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => speak(currentMessage)}
            disabled={!currentMessage.trim()}
            startIcon={<VolumeUp />}
          >
            Reproducir
          </Button>
          
          <Button
            variant="contained"
            onClick={handleTextMessage}
            disabled={!currentMessage.trim()}
            startIcon={<Send />}
            sx={{
              background: '#10b981',
              '&:hover': {
                background: '#059669'
              }
            }}
          >
            Enviar
          </Button>
        </Box>
        
        {/* Voice Status */}
        {isListening && (
          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#ff4444', fontWeight: 'bold' }}>
              🎤 Escuchando...
            </Typography>
            <Typography variant="caption" sx={{ mt: 1, color: '#666' }}>
              {transcript && `"${transcript}"`}
            </Typography>
          </Box>
        )}
        
        {fullTranscript && (
          <Box sx={{ mt: 1, p: 1, bgcolor: '#f0f0f0', borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: '#666' }}>
              Transcripción: "{fullTranscript}"
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleVoiceMessage}
              sx={{ mt: 1 }}
            >
              Enviar por voz
            </Button>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default PhoneCall;
